import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  limit,
  startAfter,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION_NAME = 'proverbs';

// Proverb model structure
export const createProverbModel = (data) => {
  return {
    id: data.id || null,
    chinese_proverb: data.chinese_proverb || '',
    pinyin: data.pinyin || '',
    english_translation: data.english_translation || '',
    english_explanation: data.english_explanation || '',
    english_equivalent: data.english_equivalent || '',
    french_translation: data.french_translation || '',
    french_explanation: data.french_explanation || '',
    french_equivalent: data.french_equivalent || '',
    arabic_translation: data.arabic_translation || '',
    arabic_Explanation: data.arabic_Explanation || '',
    created_at: data.created_at || new Date(),
    updated_at: data.updated_at || new Date(),
    created_by: data.created_by || null,
    status: data.status || 'active'
  };
};

// Validation functions
export const validateProverbUniqueness = async (proverbData, excludeId = null) => {
  try {
    const existingProverbs = await proverbService.getAllProverbs();
    
    // Check for duplicates based on multiple fields
    const duplicateFields = [];
    
    for (const existingProverb of existingProverbs) {
      // Skip if it's the same proverb being updated
      if (excludeId && existingProverb.id === excludeId) {
        continue;
      }
      
      // Check Chinese proverb
      if (proverbData.chinese_proverb && 
          existingProverb.chinese_proverb && 
          proverbData.chinese_proverb.trim() === existingProverb.chinese_proverb.trim()) {
        duplicateFields.push({
          field: 'Chinese Proverb',
          value: proverbData.chinese_proverb,
          existingId: existingProverb.id
        });
      }
      
      // Check Pinyin
      if (proverbData.pinyin && 
          existingProverb.pinyin && 
          proverbData.pinyin.trim().toLowerCase() === existingProverb.pinyin.trim().toLowerCase()) {
        duplicateFields.push({
          field: 'Pinyin',
          value: proverbData.pinyin,
          existingId: existingProverb.id
        });
      }
      
      // Check English translation
      if (proverbData.english_translation && 
          existingProverb.english_translation && 
          proverbData.english_translation.trim().toLowerCase() === existingProverb.english_translation.trim().toLowerCase()) {
        duplicateFields.push({
          field: 'English Translation',
          value: proverbData.english_translation,
          existingId: existingProverb.id
        });
      }
      
      // Check French translation
      if (proverbData.french_translation && 
          existingProverb.french_translation && 
          proverbData.french_translation.trim().toLowerCase() === existingProverb.french_translation.trim().toLowerCase()) {
        duplicateFields.push({
          field: 'French Translation',
          value: proverbData.french_translation,
          existingId: existingProverb.id
        });
      }
      
      // Check Arabic translation
      if (proverbData.arabic_translation && 
          existingProverb.arabic_translation && 
          proverbData.arabic_translation.trim() === existingProverb.arabic_translation.trim()) {
        duplicateFields.push({
          field: 'Arabic Translation',
          value: proverbData.arabic_translation,
          existingId: existingProverb.id
        });
      }
    }
    
    return {
      isUnique: duplicateFields.length === 0,
      duplicateFields
    };
  } catch (error) {
    console.error('Error validating proverb uniqueness:', error);
    // If validation fails, allow creation but log the error
    return {
      isUnique: true,
      duplicateFields: [],
      validationError: error.message
    };
  }
};

// Validate multiple proverbs for batch import
export const validateProverbsBatch = async (proverbsArray) => {
  const validationResults = [];
  const validProverbs = [];
  const duplicateProverbs = [];
  
  for (let i = 0; i < proverbsArray.length; i++) {
    const proverb = proverbsArray[i];
    
    // Validate against existing proverbs
    const uniquenessCheck = await validateProverbUniqueness(proverb);
    
    // Also check against other proverbs in the same batch
    const batchDuplicates = [];
    for (let j = i + 1; j < proverbsArray.length; j++) {
      const otherProverb = proverbsArray[j];
      
      if ((proverb.chinese_proverb && otherProverb.chinese_proverb && 
           proverb.chinese_proverb.trim() === otherProverb.chinese_proverb.trim()) ||
          (proverb.pinyin && otherProverb.pinyin && 
           proverb.pinyin.trim().toLowerCase() === otherProverb.pinyin.trim().toLowerCase()) ||
          (proverb.english_translation && otherProverb.english_translation && 
           proverb.english_translation.trim().toLowerCase() === otherProverb.english_translation.trim().toLowerCase())) {
        batchDuplicates.push({
          field: 'Duplicate in batch',
          batchIndex: j
        });
      }
    }
    
    const result = {
      index: i,
      proverb,
      isValid: uniquenessCheck.isUnique && batchDuplicates.length === 0,
      duplicateFields: [...uniquenessCheck.duplicateFields, ...batchDuplicates],
      validationError: uniquenessCheck.validationError
    };
    
    validationResults.push(result);
    
    if (result.isValid) {
      validProverbs.push(proverb);
    } else {
      duplicateProverbs.push(result);
    }
  }
  
  return {
    totalProverbs: proverbsArray.length,
    validProverbs,
    duplicateProverbs,
    validationResults
  };
};

// Firebase service for proverbs
export const proverbService = {
  // Get all proverbs (sorted by creation date, newest first)
  async getAllProverbs() {
    try {
      console.log('Attempting to fetch proverbs from Firebase...');
      
      const querySnapshot = await getDocs(
        query(
          collection(db, COLLECTION_NAME), 
          orderBy('created_at', 'desc')
        )
      );
      
      const proverbs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`Successfully fetched ${proverbs.length} proverbs from Firebase`);
      return proverbs;
    } catch (error) {
      console.error('Firebase Error:', error.message);
      
      
      
      throw error;
    }
  },


  // Get proverb by ID
  async getProverbById(id) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Proverb not found');
      }
    } catch (error) {
      console.error('Error getting proverb:', error);

      throw error;
    }
  },

  // Add new proverb with uniqueness validation
  async createProverb(proverbData) {
    try {
      // First validate uniqueness
      const validation = await validateProverbUniqueness(proverbData);
      
      if (!validation.isUnique) {
        const duplicateFields = validation.duplicateFields.map(df => df.field).join(', ');
        throw new Error(`Duplicate proverb found! The following fields already exist: ${duplicateFields}`);
      }
      
      const proverb = createProverbModel({
        ...proverbData,
        created_at: new Date(),
        updated_at: new Date()
      });

      let docRef;
      
      // If proverb has an ID, use setDoc to preserve the ID
      if (proverbData.id) {
        const customDocRef = doc(db, COLLECTION_NAME, proverbData.id.toString());
        await setDoc(customDocRef, proverb);
        docRef = customDocRef;
        console.log('✅ Proverb created successfully in Firebase with preserved ID:', proverbData.id);
      } else {
        // Use addDoc for auto-generated ID
        docRef = await addDoc(collection(db, COLLECTION_NAME), proverb);
        console.log('✅ Proverb created successfully in Firebase with ID:', docRef.id);
      }
      
      return {
        id: proverbData.id || docRef.id,
        ...proverb
      };
    } catch (error) {
      console.error('Error creating proverb:', error);
      
      if (error.message.includes('permissions') || error.message.includes('Missing or insufficient')) {
        console.log('Cannot create proverb: Firebase permissions required');
        throw new Error('Firebase permissions required for creating proverbs. Please contact administrator.');
      }
      
      throw error;
    }
  },

  // Update proverb with uniqueness validation
  async updateProverb(id, updateData) {
    try {
      // First validate uniqueness (excluding the current proverb)
      const validation = await validateProverbUniqueness(updateData, id);
      
      if (!validation.isUnique) {
        const duplicateFields = validation.duplicateFields.map(df => df.field).join(', ');
        throw new Error(`Duplicate proverb found! The following fields already exist: ${duplicateFields}`);
      }
      
      const docRef = doc(db, COLLECTION_NAME, id);
      const updatedData = {
        ...updateData,
        updated_at: new Date()
      };
      
      await updateDoc(docRef, updatedData);
      
      // Return updated proverb
      return await this.getProverbById(id);
    } catch (error) {
      console.error('Error updating proverb:', error);
      throw error;
    }
  },

  // Delete proverb
  async deleteProverb(id) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting proverb:', error);
      throw error;
    }
  },

  // Search proverbs
  async searchProverbs(searchTerm, language = null) {
    try {
      let q = collection(db, COLLECTION_NAME);
      
      // Note: Firestore doesn't support full-text search natively
      // For production, consider using Algolia or Elasticsearch
      // This is a basic implementation
      
      const querySnapshot = await getDocs(q);
      const allProverbs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter on the client side for now
      return allProverbs.filter(proverb => {
        const matchesSearch = !searchTerm || 
          proverb.chinese_proverb?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          proverb.pinyin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          proverb.english_translation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          proverb.english_explanation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          proverb.french_translation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          proverb.arabic_translation?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesLanguage = !language || 
          (language === 'chinese' && proverb.chinese_proverb) ||
          (language === 'english' && proverb.english_translation) ||
          (language === 'french' && proverb.french_translation) ||
          (language === 'arabic' && proverb.arabic_translation);

        return matchesSearch && matchesLanguage;
      });
    } catch (error) {
      console.error('Error searching proverbs:', error);
      throw error;
    }
  },

  // Get proverbs with pagination
  async getProverbsPaginated(pageSize = 10, lastDoc = null) {
    try {
      let q = query(
        collection(db, COLLECTION_NAME),
        orderBy('created_at', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      
      return {
        proverbs: querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })),
        lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1]
      };
    } catch (error) {
      console.error('Error getting paginated proverbs:', error);
      throw error;
    }
  },


  // Batch import proverbs with validation
  async importProverbsBatch(proverbsArray) {
    try {
      // Validate all proverbs first
      const validation = await validateProverbsBatch(proverbsArray);
      
      const results = {
        totalProverbs: validation.totalProverbs,
        successCount: 0,
        duplicateCount: validation.duplicateProverbs.length,
        errorCount: 0,
        duplicateDetails: validation.duplicateProverbs,
        errors: []
      };
      
      // Import only valid proverbs
      for (const proverb of validation.validProverbs) {
        try {
          await this.createProverb(proverb);
          results.successCount++;
        } catch (error) {
          results.errorCount++;
          results.errors.push({
            proverb,
            error: error.message
          });
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error importing proverbs batch:', error);
      throw error;
    }
  }
};
